// The MEMORY REVIEW surfaces (N-MEM-3/4), the Signal re-layout: the Update Proposals and the Conflicts as
// stacked decision cards, plus the Promoted Layer. Every human decision here is a ROUTED INTENT to the owning
// `memory` (C) Gate — accept/reject a Proposal, resolve a Conflict — NEVER a raw edit (the dashboard edits
// nothing and sets no approval field; P-GATE). No approval step, pending state or confirmation queue was
// added by the re-layout: these are the SAME two decisions on the SAME two routes as before. Both sides of a
// conflict render as C recorded them; the dashboard never picks a winner (N-MEM-3 AC-3). Controls disable
// while a route is in flight so a double-click cannot double-route. Tokens only (G-181).
//
// WHAT THE DESIGN SHOWS THAT THE DATA/ROUTES DO NOT SUPPORT, and is therefore ABSENT here:
//   • the proposal's "Now → Proposed" diff — a Proposal carries its proposed change only; there is no
//     before-text in the projection, so the card shows the proposed change alone rather than half a diff;
//   • the proposal's source line and reviewer note — neither is a field;
//   • the conflict's per-side "Keep this" — `conflict_resolve` carries no side, so a two-way choice would be
//     a control that cannot deliver what it promises. Both sides render; ONE resolve routes the decision.
//   • the post-decision "✓ Accepted / ✕ Rejected" tag — a decided item is re-read from C and leaves the
//     pending list, so the surface shows the recorded state rather than a client-held one (G-182).

import { useState } from 'react';
import { Btn, EmptyState, Panel, StatusPill } from '../primitives.tsx';
import { MemoryPromotedList } from './MemoryPromotedList.tsx';
import { humaniseEnum } from './memory-tree.ts';
import type { RouteResult } from '../api-client.ts';
import type { ConflictView, MemoryReviewProjection, ProposalView } from '../know-projections.ts';

export interface MemoryReviewProps {
  readonly review: MemoryReviewProjection;
  /** Route a proposal decision (accept/reject) to C — the dashboard never writes Memory. */
  readonly onDecideProposal: (
    proposalId: string,
    decision: 'accepted' | 'rejected',
  ) => Promise<RouteResult>;
  /** Route a conflict resolution to C — a human Gate; the agent never picks a winner. */
  readonly onResolveConflict: (conflictId: string) => Promise<RouteResult>;
  /** Open a Node the card names in the reader (the same deep-focus a chat memory trace uses). */
  readonly onOpenNode: (nodeId: string) => void;
  /** When true, the decide/resolve controls render READ-ONLY (disabled): this account's decision was
   *  refused by the owning Gate (the Memory-decision capability is operator-only by design), so the surface
   *  stops offering an active control that would only 403 again. The honest note is rendered by the pane. */
  readonly decideDisabled?: boolean;
}

/** Which review list is shown (a subtab id — the surface owns the tab state). */
export type ReviewTab = 'proposals' | 'conflicts' | 'promoted';

/** Render the active review list. */
export function MemoryReview({
  tab,
  review,
  onDecideProposal,
  onResolveConflict,
  onOpenNode,
  decideDisabled = false,
}: MemoryReviewProps & { readonly tab: ReviewTab }): React.JSX.Element {
  if (tab === 'proposals') {
    return (
      <ProposalList
        proposals={review.proposals}
        onDecide={onDecideProposal}
        onOpenNode={onOpenNode}
        decideDisabled={decideDisabled}
      />
    );
  }
  if (tab === 'conflicts') {
    return (
      <ConflictList
        conflicts={review.conflicts}
        onResolve={onResolveConflict}
        onOpenNode={onOpenNode}
        decideDisabled={decideDisabled}
      />
    );
  }
  return <MemoryPromotedList promoted={review.promoted} onOpenNode={onOpenNode} />;
}

/** Track a route-in-flight busy flag so a decision control cannot double-route on a rapid double-click. */
function useBusy(): {
  readonly busy: boolean;
  readonly run: (fn: () => Promise<unknown>) => Promise<void>;
} {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

/** The "open the memory this card is about" link — rendered only when the card names EXACTLY one Node, so
 *  the control's meaning is unambiguous and no raw id is ever printed (G-197). */
function TargetLink({
  targetNodeIds,
  onOpenNode,
}: {
  readonly targetNodeIds: readonly string[];
  readonly onOpenNode: (nodeId: string) => void;
}): React.JSX.Element | null {
  const only = targetNodeIds.length === 1 ? targetNodeIds[0] : undefined;
  if (only === undefined) return null;
  return (
    <button
      type="button"
      className="mw-link"
      aria-label="Open the target memory"
      onClick={() => onOpenNode(only)}
    >
      <span aria-hidden="true">→ </span>
      Open the target memory
    </button>
  );
}

/** The pending Update Proposals — accept/reject each individually. */
function ProposalList({
  proposals,
  onDecide,
  onOpenNode,
  decideDisabled,
}: {
  readonly proposals: readonly ProposalView[];
  readonly onDecide: (id: string, decision: 'accepted' | 'rejected') => Promise<RouteResult>;
  readonly onOpenNode: (nodeId: string) => void;
  readonly decideDisabled: boolean;
}): React.JSX.Element {
  if (proposals.length === 0) {
    return (
      <Panel>
        <EmptyState>No update proposals awaiting a decision.</EmptyState>
      </Panel>
    );
  }
  return (
    <div className="mw-review" data-review-list="proposals">
      <p className="mw-lead">
        When Ritona learns something that changes an existing memory it proposes the edit here first
        — nothing is rewritten silently. Accept or reject each individually; a rejection leaves the
        original untouched.
      </p>
      {proposals.map((proposal) => (
        <ProposalCard
          key={proposal.id}
          proposal={proposal}
          onDecide={onDecide}
          onOpenNode={onOpenNode}
          decideDisabled={decideDisabled}
        />
      ))}
    </div>
  );
}

/** The two routed decisions on one Proposal. Disabled while a route is in flight (no double-route) and
 *  while the decide capability has been refused (F6) — the surface stops offering a control that only 403s. */
function ProposalActions({
  proposal,
  onDecide,
  decideDisabled,
}: {
  readonly proposal: ProposalView;
  readonly onDecide: (id: string, decision: 'accepted' | 'rejected') => Promise<RouteResult>;
  readonly decideDisabled: boolean;
}): React.JSX.Element {
  const { busy, run } = useBusy();
  const decide = (decision: 'accepted' | 'rejected'): Promise<void> =>
    run(() => onDecide(proposal.id, decision));
  return (
    <div className="mw-actions">
      <Btn
        sm
        disabled={busy || decideDisabled}
        ariaLabel={`Accept proposal: ${proposal.kind}`}
        onClick={() => void decide('accepted')}
      >
        Accept — update memory
      </Btn>
      <Btn
        kind="quiet"
        sm
        disabled={busy || decideDisabled}
        ariaLabel={`Reject proposal: ${proposal.kind}`}
        onClick={() => void decide('rejected')}
      >
        Reject
      </Btn>
    </div>
  );
}

/** One Proposal card — the kind, the target count, the proposed change, and the two routed decisions. */
function ProposalCard({
  proposal,
  onDecide,
  onOpenNode,
  decideDisabled,
}: {
  readonly proposal: ProposalView;
  readonly onDecide: (id: string, decision: 'accepted' | 'rejected') => Promise<RouteResult>;
  readonly onOpenNode: (nodeId: string) => void;
  readonly decideDisabled: boolean;
}): React.JSX.Element {
  const change = summariseChange(proposal.proposedChange);
  return (
    <div className="mw-card" data-proposal={proposal.id}>
      <div className="mw-card-top">
        <span className="mw-eyebrow">Proposed {proposal.kind}</span>
        <TargetLink targetNodeIds={proposal.targetNodeIds} onOpenNode={onOpenNode} />
        <span className="spacer" aria-hidden="true" />
        <span className="mono-label">
          {proposal.targetNodeIds.length} target node
          {proposal.targetNodeIds.length === 1 ? '' : 's'}
        </span>
      </div>
      {change !== null ? (
        <div className="mw-change">
          <span className="k">Proposed</span>
          <span className="v">{change}</span>
        </div>
      ) : null}
      <ProposalActions proposal={proposal} onDecide={onDecide} decideDisabled={decideDisabled} />
    </div>
  );
}

/** The OPEN Conflict items — both sides shown; resolution is a routed human Gate. */
function ConflictList({
  conflicts,
  onResolve,
  onOpenNode,
  decideDisabled,
}: {
  readonly conflicts: readonly ConflictView[];
  readonly onResolve: (id: string) => Promise<RouteResult>;
  readonly onOpenNode: (nodeId: string) => void;
  readonly decideDisabled: boolean;
}): React.JSX.Element {
  if (conflicts.length === 0) {
    return (
      <Panel>
        <EmptyState>No open conflicts. Memory is not holding any contested fact.</EmptyState>
      </Panel>
    );
  }
  return (
    <div className="mw-review" data-review-list="conflicts">
      <p className="mw-lead">
        Two sources disagree. Ritona flags the contradiction and holds — it will not silently pick a
        winner.
      </p>
      {conflicts.map((conflict) => (
        <ConflictCard
          key={conflict.id}
          conflict={conflict}
          onResolve={onResolve}
          onOpenNode={onOpenNode}
          decideDisabled={decideDisabled}
        />
      ))}
    </div>
  );
}

/** One Conflict card — the class, both recorded sides side by side, and the routed resolution.
 *
 *  The resolve note says only what resolving ACTUALLY does (memory.resolveConflictItem): it closes the item
 *  and records the human who settled it, so the contested fact stops blocking a promotion. It does NOT pick,
 *  retire or rewrite a side — the route carries none. The design's copy ("the other is retired, with its
 *  provenance kept") belonged to the keep-A/keep-B control this surface correctly does not offer; repeating
 *  it here would promise behaviour the platform does not perform (P-SELF). */
function ConflictCard({
  conflict,
  onResolve,
  onOpenNode,
  decideDisabled,
}: {
  readonly conflict: ConflictView;
  readonly onResolve: (id: string) => Promise<RouteResult>;
  readonly onOpenNode: (nodeId: string) => void;
  readonly decideDisabled: boolean;
}): React.JSX.Element {
  const { busy, run } = useBusy();
  return (
    <div className="mw-card">
      <div className="mw-card-top">
        <span className="mw-eyebrow">Conflict</span>
        <TargetLink targetNodeIds={conflict.targetNodeIds} onOpenNode={onOpenNode} />
        <span className="spacer" aria-hidden="true" />
        {conflict.blocksPromotionOf !== null ? (
          <StatusPill tone="attention">Blocks a promotion</StatusPill>
        ) : null}
      </div>
      <span className="mw-fact">{humaniseEnum(conflict.conflictClass)}</span>
      <div className="mw-sides" data-conflict={conflict.id}>
        {Object.entries(conflict.sides).map(([key, value]) => (
          <div key={key} className="mw-side">
            <span className="mono-label">{key}</span>
            <span className="txt">{String(value)}</span>
          </div>
        ))}
      </div>
      <div className="mw-actions">
        <Btn
          kind="ghost"
          sm
          disabled={busy || decideDisabled}
          ariaLabel="Resolve conflict"
          onClick={() => void run(() => onResolve(conflict.id))}
        >
          Resolve
        </Btn>
        <span className="mw-note">
          Resolving closes this item and records who settled it. Neither side is edited or removed
          here.
        </span>
      </div>
    </div>
  );
}

/**
 * A short human summary of a proposal's change payload — display only, never an internal shape (G-197).
 * Renders the proposal's own TEXT when it carries any (deep-sweep 2026-07-16 N8: printing the field
 * NAMES asked the human to accept a change they could not read): a `summary` verbatim, else the
 * edit/merge successor's proposed `body` as a bounded excerpt; other payload shapes fall back to
 * naming which fields change.
 */
function summariseChange(change: Readonly<Record<string, unknown>>): string | null {
  const summary = change['summary'];
  if (typeof summary === 'string' && summary.trim().length > 0) return summary;
  const body = change['body'];
  if (typeof body === 'string' && body.trim().length > 0) {
    const flat = body.replace(/\s+/g, ' ').trim();
    return flat.length > 220 ? `${flat.slice(0, 220)}…` : flat;
  }
  const keys = Object.keys(change);
  if (keys.length === 0) return null;
  return `Proposed change: ${keys.join(', ')}`;
}
